import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowLeft,
  CheckCheck,
  Clock,
  CornerUpLeft,
  Forward,
  Inbox,
  Mail as MailIcon,
  Mic,
  Paperclip,
  Plus,
  Reply,
  ReplyAll,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type MailDetail, type MailFolder, type MailPerson, type MailRow, type Paginated } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDateTime, formatNumber, relativeTime } from '../lib/format';
import { AttachmentList } from '../components/Attachments';
import { RichText } from '../components/RichTextLazy';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Tabs,
  useToast,
} from '../components/ui';

/**
 * Internal mail (operator, 2026-10-09: "any employee can send mail (message not
 * real email it's inside system only) to any other employee(s)"). Each person
 * has an Inbox, Sent and a Trash of their own; what one person does with their
 * copy of a mail never moves anyone else's.
 */

export const FOLDER_PATH: Record<MailFolder, string> = {
  inbox: '/mail',
  sent: '/mail/sent',
  trash: '/mail/trash',
};

const FOLDERS: MailFolder[] = ['inbox', 'sent', 'trash'];

/** Two letters for someone's circle: their initials, in whichever script their name is in. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('');
}

function Avatar({ name, strong = false }: { name: string; strong?: boolean }) {
  return (
    <span
      className={clsx(
        'grid h-9 w-9 shrink-0 place-items-center rounded-full text-caption font-semibold',
        // The brand badge's tones, which read in both themes.
        strong ? 'bg-primary/15 text-primary-600 dark:text-primary-400' : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
      )}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// A folder
// ---------------------------------------------------------------------------

/**
 * Routes: `/mail` (Inbox), `/mail/sent` and `/mail/trash` — one folder of the
 * caller's own mail, newest first, with a search across subjects, content and
 * names. The Inbox marks what is unread and can show only that.
 */
export function MailboxPage({ folder }: { folder: MailFolder }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const onlyUnread = folder === 'inbox' && unreadOnly;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mailbox', 'list', folder, page, q, onlyUnread],
    queryFn: () =>
      api.get<Paginated<MailRow> & { unread: number }>('/mailbox', {
        folder,
        page,
        pageSize: 25,
        q,
        unread: onlyUnread ? 'true' : undefined,
      }),
  });

  const readAll = useMutation({
    mutationFn: () => api.post('/mailbox/read-all'),
    onSuccess: () => {
      toast.success(t('mail.allRead'));
      void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const newButton = (
    <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/mail/new')}>
      {t('mail.new')}
    </Button>
  );
  const empty = onlyUnread
    ? { title: t('mail.empty.unread'), hint: t('mail.empty.unreadHint') }
    : { title: t(`mail.empty.${folder}`), hint: t(`mail.empty.${folder}Hint`) };
  const unread = data?.unread ?? 0;

  return (
    <>
      <PageHeader title={t('mail.title')} subtitle={t('mail.subtitle')} action={newButton} />

      <Tabs
        active={folder}
        onChange={(id) => navigate(FOLDER_PATH[id as MailFolder])}
        tabs={FOLDERS.map((f) => ({
          id: f,
          label: t(`mail.folder.${f}`),
          count: f === 'inbox' && unread > 0 ? unread : undefined,
        }))}
      />

      <Card padded={false} className="mt-5">
        <div className="flex flex-wrap items-center gap-3 border-b hairline p-4">
          <Input
            className="max-w-sm flex-1 basis-56"
            placeholder={t('mail.search')}
            aria-label={t('mail.search')}
            value={q}
            dir="auto"
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
          {folder === 'inbox' && (
            <div className="ms-auto flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={unreadOnly ? 'primary' : 'secondary'}
                aria-pressed={unreadOnly}
                onClick={() => {
                  setUnreadOnly((v) => !v);
                  setPage(1);
                }}
              >
                {t('mail.unreadOnly')}
              </Button>
              {unread > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<CheckCheck className="h-4 w-4" />}
                  loading={readAll.isPending}
                  onClick={() => readAll.mutate()}
                >
                  {t('mail.markAllRead')}
                </Button>
              )}
            </div>
          )}
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={folder === 'sent' ? <Send className="h-8 w-8 flip-rtl" /> : folder === 'trash' ? <Trash2 className="h-8 w-8" /> : <Inbox className="h-8 w-8" />}
            title={q ? undefined : empty.title}
            hint={q ? undefined : empty.hint}
            action={!q && folder !== 'trash' && !onlyUnread ? newButton : undefined}
          />
        ) : (
          <ul>
            {data.items.map((mail) => (
              <MailItem key={mail.id} mail={mail} />
            ))}
          </ul>
        )}
        {data && <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />}
      </Card>
      <span className="sr-only" aria-live="polite">
        {unread > 0 ? t('mail.unreadCount', { n: formatNumber(unread, locale) }) : ''}
      </span>
    </>
  );
}

/** "A, B, C +2" — the first few names, and how many more. */
function useNames() {
  const { t, locale, localized } = useI18n();
  return (people: MailPerson[], total: number) => {
    const names = people.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
    return total > people.length ? `${names} ${t('mail.more', { n: formatNumber(total - people.length, locale) })}` : names;
  };
}

/** How a sent mail has landed: "Read by 1 of 3", "Read by everyone", "Not read yet". */
function ReadState({ read, total }: { read: number; total: number }) {
  const { t, locale } = useI18n();
  const n = (value: number) => formatNumber(value, locale);
  if (read === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-faint">
        <Clock className="h-3.5 w-3.5" aria-hidden />
        {t('mail.notReadYet')}
      </span>
    );
  }
  return (
    <span className={clsx('inline-flex items-center gap-1', read === total ? 'text-success-fg dark:text-success' : 'text-muted')}>
      <CheckCheck className="h-3.5 w-3.5" aria-hidden />
      {total === 1 ? t('mail.read') : read === total ? t('mail.readByAll') : t('mail.readBy', { read: n(read), total: n(total) })}
    </span>
  );
}

function MailItem({ mail }: { mail: MailRow }) {
  const { t, locale, localized } = useI18n();
  const names = useNames();
  const count = (n: number) => formatNumber(n, locale);
  // A mail of one's own shows whom it went to; a mail received, who sent it.
  const who = mail.mine ? t('mail.toNames', { names: names(mail.to, mail.toCount) }) : localized(mail.sender);
  const face = mail.mine ? localized(mail.to[0]) : localized(mail.sender);

  return (
    <li className="border-b hairline last:border-0">
      <Link
        to={`/mail/${mail.id}`}
        className={clsx(
          'flex gap-3 px-4 py-3.5 transition-colors duration-fast hover:bg-[var(--surface-sunken)] sm:px-5',
          mail.unread && 'bg-primary/5',
        )}
      >
        <span className="relative">
          <Avatar name={face} strong={mail.unread} />
          {mail.unread && (
            <span className="absolute -end-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-[var(--surface-card)]" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-3">
            <span
              className={clsx(
                'min-w-0 flex-1 truncate text-body-sm text-[var(--text-primary)]',
                mail.unread ? 'font-semibold' : 'font-medium',
              )}
            >
              <bdi>{who}</bdi>
            </span>
            <span className="shrink-0 text-caption text-muted tabular" title={formatDateTime(mail.createdAt, locale)}>
              {relativeTime(mail.createdAt, locale)}
            </span>
          </span>
          <span className="mt-0.5 flex min-w-0 items-center gap-2">
            {mail.unread && (
              <Badge tone="brand" className="shrink-0">
                {t('mail.newBadge')}
              </Badge>
            )}
            {/* Written in either language: it reads in its own direction, whatever the page's. */}
            <span
              className={clsx('min-w-0 truncate text-body text-[var(--text-primary)]', mail.unread ? 'font-semibold' : 'font-medium')}
              dir="auto"
            >
              {mail.subject}
            </span>
          </span>
          {mail.excerpt && (
            <span className="mt-0.5 block truncate text-body-sm text-muted" dir="auto">
              {mail.excerpt}
            </span>
          )}
          {(mail.files > 0 || mail.voiceNotes > 0 || mail.isReply || mail.isForward || mail.readCount !== null) && (
            <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-muted">
              {mail.isReply && (
                <span className="inline-flex items-center gap-1">
                  <CornerUpLeft className="h-3.5 w-3.5 flip-rtl" aria-hidden />
                  {t('mail.replyBadge')}
                </span>
              )}
              {mail.isForward && (
                <span className="inline-flex items-center gap-1">
                  <Forward className="h-3.5 w-3.5 flip-rtl" aria-hidden />
                  {t('mail.forwardBadge')}
                </span>
              )}
              {mail.files > 0 && (
                <Meta icon={<Paperclip className="h-3.5 w-3.5" />} label={t('mail.files')}>
                  {count(mail.files)}
                </Meta>
              )}
              {mail.voiceNotes > 0 && (
                <Meta icon={<Mic className="h-3.5 w-3.5" />} label={t('mail.voiceNotes')}>
                  {count(mail.voiceNotes)}
                </Meta>
              )}
              {mail.readCount !== null && <ReadState read={mail.readCount} total={mail.toCount} />}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}

function Meta({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 tabular" title={label}>
      <span aria-hidden>{icon}</span>
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// One mail
// ---------------------------------------------------------------------------

type Action = 'unread' | 'trash' | 'restore' | 'purge';

/** Beyond this many people, the rest of the list opens on request. */
const PEOPLE_SHOWN = 12;

/**
 * Route: `/mail/:id` — where the bell and the folders land. Opening a mail sent
 * to you reads it. From here: Reply, Reply all, Forward, Mark as unread, and
 * Delete (to the Trash) — or, in the Trash, Restore and Delete for good, which
 * asks once more in place.
 */
export function MailDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [allPeople, setAllPeople] = useState(false);

  const act = useMutation({
    mutationFn: (action: Action) =>
      action === 'purge' ? api.del(`/mailbox/${id}`) : api.post(`/mailbox/${id}/${action}`),
    onSuccess: (_data, action) => {
      void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      const mail = query.data;
      // Stale, not removed: removing the mail of a page still on screen fetches it
      // again at once, and opening it is what reads it — "Mark as unread" would
      // undo itself. Coming back to it later opens it afresh.
      const stale = () => void queryClient.invalidateQueries({ queryKey: ['mail', id], refetchType: 'none' });
      switch (action) {
        case 'unread':
          stale();
          toast.success(t('mail.markedUnread'));
          navigate(FOLDER_PATH.inbox);
          break;
        case 'trash':
          stale();
          toast.success(t('mail.movedToTrash'));
          navigate(FOLDER_PATH[mail?.mine ? 'sent' : 'inbox']);
          break;
        case 'restore':
          toast.success(mail?.mine ? t('mail.restoredSent') : t('mail.restoredInbox'));
          void query.refetch();
          break;
        case 'purge':
          toast.success(t('mail.deletedForever'));
          navigate(FOLDER_PATH.trash, { replace: true });
          break;
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const query = useQuery({
    queryKey: ['mail', id],
    queryFn: () => api.get<MailDetail>(`/mailbox/${id}`),
    // Once deleted for good it no longer opens: nothing left to load.
    enabled: !(act.isSuccess && act.variables === 'purge'),
  });
  const { data: mail, isLoading, error, refetch } = query;

  // Opening it read it: the folders, the unread count and the bell catch up.
  const opened = mail?.id;
  useEffect(() => {
    if (!opened) return;
    void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [opened, queryClient]);

  if (isLoading) return <LoadingBlock />;
  if (error || !mail) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const folder = mail.folder ?? 'inbox';
  const people = allPeople ? mail.recipients : mail.recipients.slice(0, PEOPLE_SHOWN);
  const firstAnswer = mail.can.reply ? 'reply' : mail.can.replyAll ? 'replyAll' : null;
  const busy = act.isPending ? act.variables : null;

  return (
    <div className="max-w-4xl">
      <div className="mb-4">
        <Link
          to={FOLDER_PATH[folder]}
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t(`mail.folder.${folder}`)}
        </Link>
      </div>

      <PageHeader
        title={<span dir="auto">{mail.subject}</span>}
        action={
          <>
            {mail.can.reply && (
              <Button
                variant={firstAnswer === 'reply' ? 'primary' : 'secondary'}
                icon={<Reply className="h-4 w-4 flip-rtl" />}
                onClick={() => navigate(`/mail/new?reply=${mail.id}`)}
              >
                {t('mail.reply')}
              </Button>
            )}
            {mail.can.replyAll && (
              <Button
                variant={firstAnswer === 'replyAll' ? 'primary' : 'secondary'}
                icon={<ReplyAll className="h-4 w-4 flip-rtl" />}
                onClick={() => navigate(`/mail/new?replyAll=${mail.id}`)}
              >
                {t('mail.replyAll')}
              </Button>
            )}
            {mail.can.forward && (
              <Button icon={<Forward className="h-4 w-4 flip-rtl" />} onClick={() => navigate(`/mail/new?forward=${mail.id}`)}>
                {t('mail.forward')}
              </Button>
            )}
            {mail.can.markUnread && (
              <Button icon={<MailIcon className="h-4 w-4" />} loading={busy === 'unread'} onClick={() => act.mutate('unread')}>
                {t('mail.markUnread')}
              </Button>
            )}
            {mail.can.trash && (
              <Button icon={<Trash2 className="h-4 w-4" />} loading={busy === 'trash'} onClick={() => act.mutate('trash')}>
                {t('mail.delete')}
              </Button>
            )}
            {mail.can.restore && (
              <Button icon={<Undo2 className="h-4 w-4 flip-rtl" />} loading={busy === 'restore'} onClick={() => act.mutate('restore')}>
                {t('mail.restore')}
              </Button>
            )}
            {mail.can.purge && (
              <Button variant="danger" icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirming(true)}>
                {t('mail.deleteForever')}
              </Button>
            )}
          </>
        }
      />

      {folder === 'trash' && (
        <p className="mb-4 rounded-md border border-warning/35 bg-warning/8 p-3 text-body-sm text-[var(--text-primary)]">
          {t('mail.inTrash')}
        </p>
      )}

      {/* Asked in place — no dialog. */}
      {confirming && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-error/35 bg-error/10 px-3 py-2">
          <span className="min-w-0 flex-1 basis-60 text-body-sm text-error-fg dark:text-error-dark">{t('mail.deleteForeverConfirm')}</span>
          <Button size="sm" onClick={() => setConfirming(false)}>
            {t('mail.keep')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={busy === 'purge'}
            onClick={() => {
              act.mutate('purge');
              setConfirming(false);
            }}
          >
            {t('mail.deleteForever')}
          </Button>
        </div>
      )}

      <Card>
        <div className="flex items-start gap-3">
          <Avatar name={localized(mail.sender)} strong />
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-body font-semibold text-[var(--text-primary)]">
                <bdi>{localized(mail.sender)}</bdi>
              </span>
              {mail.sender.id === user?.id ? (
                <span className="text-caption text-muted">({t('mail.you')})</span>
              ) : (
                mail.sender.jobTitle && <span className="text-caption text-muted">{mail.sender.jobTitle}</span>
              )}
              {!mail.sender.isActive && <Badge tone="neutral">{t('mail.left')}</Badge>}
            </p>
            <p className="text-caption text-muted tabular">
              <span title={formatDateTime(mail.createdAt, locale)}>{formatDateTime(mail.createdAt, locale)}</span>
              <span aria-hidden> · </span>
              {relativeTime(mail.createdAt, locale)}
            </p>

            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-body-sm text-muted">{t('mail.to')}</span>
              {people.map((p) => (
                <span
                  key={p.id}
                  className={clsx(
                    'inline-flex max-w-full items-center gap-1.5 rounded-pill px-2.5 py-0.5 text-caption ring-1 ring-inset',
                    p.isActive
                      ? 'bg-neutral-500/10 text-[var(--text-secondary)] ring-neutral-500/25'
                      : 'bg-transparent text-faint ring-neutral-500/20 line-through decoration-[var(--text-tertiary)]',
                  )}
                  title={p.isActive ? p.jobTitle ?? undefined : t('mail.left')}
                >
                  <bdi className="truncate">{p.id === user?.id ? `${localized(p)} (${t('mail.you')})` : localized(p)}</bdi>
                  {/* How it landed with each — whoever sent it sees it, nobody else. */}
                  {mail.mine &&
                    (p.readAt ? (
                      <span title={t('mail.readAt', { when: formatDateTime(p.readAt, locale) })} className="text-success-fg dark:text-success">
                        <CheckCheck className="h-3.5 w-3.5" aria-hidden />
                        <span className="sr-only">{t('mail.readAt', { when: formatDateTime(p.readAt, locale) })}</span>
                      </span>
                    ) : (
                      <span title={t('mail.notReadYet')} className="text-faint">
                        <Clock className="h-3.5 w-3.5" aria-hidden />
                        <span className="sr-only">{t('mail.notReadYet')}</span>
                      </span>
                    ))}
                </span>
              ))}
              {mail.recipients.length > PEOPLE_SHOWN && (
                <button
                  type="button"
                  onClick={() => setAllPeople((v) => !v)}
                  className="text-caption font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {allPeople
                    ? t('announcements.showLess')
                    : t('announcements.showAll', { n: formatNumber(mail.recipients.length, locale) })}
                </button>
              )}
            </div>
            {mail.mine && mail.recipients.length > 1 && (
              <p className="mt-1.5 text-caption">
                <ReadState read={mail.recipients.filter((p) => p.readAt).length} total={mail.recipients.length} />
              </p>
            )}

            {(mail.replyTo || mail.forwardOf) && (
              <p className="mt-2 flex min-w-0 items-center gap-1.5 text-caption text-muted">
                {mail.replyTo ? (
                  <CornerUpLeft className="h-3.5 w-3.5 shrink-0 flip-rtl" aria-hidden />
                ) : (
                  <Forward className="h-3.5 w-3.5 shrink-0 flip-rtl" aria-hidden />
                )}
                <span className="shrink-0">{mail.replyTo ? t('mail.inReplyTo') : t('mail.forwardOf')}</span>
                <Link
                  to={`/mail/${(mail.replyTo ?? mail.forwardOf)!.id}`}
                  className="min-w-0 truncate text-primary-600 dark:text-primary-400 hover:underline"
                  dir="auto"
                >
                  {(mail.replyTo ?? mail.forwardOf)!.subject}
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 border-t hairline pt-5">
          {mail.bodyHtml ? (
            <RichText html={mail.bodyHtml} />
          ) : (
            <p className="text-body-sm text-muted">{t('mail.noContent')}</p>
          )}
        </div>

        {mail.attachments.length > 0 && (
          <div className="mt-5 border-t hairline pt-4">
            <p className="mb-2 flex items-center gap-2 text-body-sm font-medium text-[var(--text-secondary)]">
              <Paperclip className="h-4 w-4" aria-hidden />
              {t('attachments.title')}
              <Badge tone="neutral" className="tabular">
                {formatNumber(mail.attachments.length, locale)}
              </Badge>
            </p>
            <AttachmentList attachments={mail.attachments} />
          </div>
        )}
      </Card>
    </div>
  );
}
